import * as React from 'react';
import { Badge } from '../../../Components/Badge';
import { Card, CardContent } from '../../../Components/Card';
import Loader from '../../../Components/Loader';
import { Modal } from '../../../Components/Modal';
import { ScrollArea } from '../../../Components/ScrollArea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../Components/Table';
import { Button } from '../../../Components/Button';
import { AdminIcon } from '../../../Icons/AdminIcon';
import { FreebieDownloadDto, FreebieService, FreebieSummaryDto } from '../../../DataService/FreebieService';
import { toast } from 'react-toastify';

type Props = {
  isLoading: boolean;
  freebies: FreebieSummaryDto[];
  onCreate: () => void;
  onEdit: (freebie: FreebieSummaryDto) => void;
  onDelete: (id: string) => void;
};

export const FreebieCardView = ({ isLoading, freebies, onCreate, onEdit, onDelete }: Props) => {
  const getShareLink = (token: string) => `${window.location.origin}/freebie/${token}`;
  const [downloadsOpen, setDownloadsOpen] = React.useState(false);
  const [selectedFreebie, setSelectedFreebie] = React.useState<FreebieSummaryDto | null>(null);
  const [downloads, setDownloads] = React.useState<FreebieDownloadDto[]>([]);
  const [isLoadingDownloads, setIsLoadingDownloads] = React.useState(false);

  const copyToClipboard = async (value: string) => {
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        toast.success('Länk kopierad');
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (!copied) {
        throw new Error('Copy command failed');
      }

      toast.success('Länk kopierad');
    } catch (error) {
      console.error('[FreebieCardView] Copy link failed', error);
      toast.error('Kunde inte kopiera länken i den här webbläsaren.');
    }
  };

  const openDownloads = async (freebie: FreebieSummaryDto) => {
    setSelectedFreebie(freebie);
    setDownloadsOpen(true);
    setIsLoadingDownloads(true);
    setDownloads([]);

    try {
      const items = await FreebieService.getDownloads(freebie.id);
      setDownloads(items ?? []);
    } catch (error) {
      console.error('[FreebieCardView] Failed to load downloads', error);
      toast.error('Kunde inte hämta nedladdningarna.');
    } finally {
      setIsLoadingDownloads(false);
    }
  };

  const closeDownloads = () => {
    setDownloadsOpen(false);
    setSelectedFreebie(null);
    setDownloads([]);
    setIsLoadingDownloads(false);
  };

  return (
    <Card className="admin-surface-card admin-campain-list-card" disableHover>
      <div className="admin-campain-list-header">
        <div>
          <h3>Alla freebies</h3>
          <p>Skapa, redigera och ta bort freebies i samma arbetsyta.</p>
        </div>
        <Button onClick={onCreate} className="submit-button" text={<span style={{display:'flex',justifyContent:'center',gap:8}}><AdminIcon.Gifts fill='white' height={16}/>Skapa freebie</span>} />
      </div>
      <CardContent>
        <ScrollArea className="h-[500px]">
          {isLoading ? <Loader loaderSize="XLARGE" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delningskod</TableHead>
                  <TableHead>Länk</TableHead>
                  <TableHead>Skapad</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead>Användning</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Åtgärder</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {freebies.map((code) => (
                  <TableRow key={code.id}>
                  <TableCell>{code.publicToken}</TableCell>
                  <TableCell>
                    <button type="button" className='bla-cell-button-one' onClick={() => void copyToClipboard(getShareLink(code.publicToken))}>
                        Kopiera länk
                      </button>
                    </TableCell>
                    <TableCell className="font-mono font-semibold">{new Date(code.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{code.title}</TableCell>
                    <TableCell className="max-w-xs truncate">{code.downloadCount}</TableCell>
                    <TableCell>{code.isActive ? <Badge className="bg-green-500">Aktiv</Badge> : <Badge variant="secondary">Inaktiv</Badge>}</TableCell>
                    <TableCell className="text-right" style={{ display: 'flex', gap: 4 }}>
                       <button type="button" className='bla-cell-button-one' onClick={() => void openDownloads(code)}>Nedladdningar</button>
                       <button type="button" className='bla-cell-button-one' onClick={() => onEdit(code)}><AdminIcon.EditPencil width={14} height={14} fill='hsl(var(--primary))'/></button>
                       <button type="button" className='bla-cell-button-one' onClick={() => onDelete(code.id)}><AdminIcon.Trashcan width={14} height={14} fill='hsl(var(--primary))'/></button>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </CardContent>

      {downloadsOpen && selectedFreebie && (
        <Modal onClose={closeDownloads} className="modal-content-large" style={{ maxWidth: 1100 }}>
          <div className="email-template-modal">
            <div className="email-template-modal__header">
              <div>
                <h2 className="email-template-modal__title">Nedladdningar</h2>
                <p>{selectedFreebie.title}</p>
              </div>
              <button type="button" className="email-template-modal__close" onClick={closeDownloads}>✕</button>
            </div>

            <div className="email-template-modal__body">
              {isLoadingDownloads ? (
                <Loader loaderSize="MEDIUM" />
              ) : downloads.length === 0 ? (
                <p>Inga nedladdningar ännu.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Nedladdad</th>
                        <th className="py-2 pr-3">Skapad</th>
                        <th className="py-2 pr-3">Consent</th>
                        <th className="py-2 pr-3">IP</th>
                        <th className="py-2 pr-3">User agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloads.map((item) => (
                        <tr key={item.id} className="border-b align-top">
                          <td className="py-2 pr-3 font-medium">{item.email}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{item.downloadedAt ? new Date(item.downloadedAt).toLocaleString() : '-'}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{new Date(item.createdAt).toLocaleString()}</td>
                          <td className="py-2 pr-3">{item.marketingConsent ? 'Ja' : 'Nej'}</td>
                          <td className="py-2 pr-3 font-mono text-xs">{item.ipAddress ?? '-'}</td>
                          <td className="py-2 pr-3 text-xs max-w-[24rem] break-words">{item.userAgent ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
};
